import { useState } from 'react';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { RadioLabel, RadioOptionWrapper, RadioBlockGroup } from 'design-system/src/components/Radio/Radio';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ActionCard } from '../../ActionCard';
import { getPharmacyOptionsFromSuggestionsList } from '../utils';
import type { CashPricePharmacySuggestion } from '../types';
import { OtherRequestButton } from './shared/OtherRequestButton';
import { Step, updatedStepsOnSelect, isOptionChecked } from './shared/helpers';
import { RadioLabelAndSubheader, RadioOptionWithSubheader, RadioOptionWithSubheaderWrapper } from './shared/styles';

type OfferedBestCashPriceActionsProps = {
  setShowDeclineModal: (show: boolean) => void;
  setShowNewFollowupTaskModal: (show: boolean) => void;
  handleTaskResponse: (response: string) => Promise<void>;
  cashPricePharmacySuggestions: CashPricePharmacySuggestion[];
};

export const OfferedBestCashPriceActions = ({
  setShowDeclineModal,
  setShowNewFollowupTaskModal,
  handleTaskResponse,
  cashPricePharmacySuggestions,
}: OfferedBestCashPriceActionsProps) => {
  const steps: Step[] = [];

  const pharmacyOptions = getPharmacyOptionsFromSuggestionsList(cashPricePharmacySuggestions);

  if (pharmacyOptions.cheapestPharmacy) {
    steps.push({
      label: 'Continue with the best available cash price',
      subheader: `Tandem will send the prescription to ${pharmacyOptions.cheapestPharmacy.pharmacy_name} at $${pharmacyOptions.cheapestPharmacy.price} and inform the patient`,
      value: 'approve_cash_price',
      checked: true,
    });
  } else {
    // If the task is missing the cash price pharmacy suggestions metadata, show a generic message
    steps.push({
      label: 'Continue with the best available cash price',
      subheader: 'Tandem will send the prescription to pharmacy and inform the patient',
      value: 'approve_cash_price',
      checked: true,
    });
  }
  // The additional preferred pharmacy step is only shown if there are multiple suggestions and the preferred pharmacy is different from the cheapest pharmacy
  if (pharmacyOptions.preferredPharmacy) {
    steps.push({
      label: "Continue with the patient's preferred pharmacy",
      subheader: `Tandem will send the prescription to ${pharmacyOptions.preferredPharmacy.pharmacy_name} at $${pharmacyOptions.preferredPharmacy.price} and inform the patient`,
      value: 'approve_cash_price_preferred_pharmacy',
      checked: false,
    });
  }
  steps.push({
    label: 'Close this task and cancel the prescription',
    value: 'cancel_prescription',
    checked: false,
  });
  const [selectedOption, setSelectedOption] = useState<Step[]>(steps);

  const handleContinue = () => {
    if (isOptionChecked(selectedOption, 'cancel_prescription')) {
      // Open decline modal which will prompt for cancel reason
      setShowDeclineModal(true);
    } else {
      const selectedValue = selectedOption.find((option) => option.checked)?.value;
      handleTaskResponse(selectedValue ?? 'approve_cash_price');
    }
  };

  return (
    <ActionCard>
      <RadioBlockGroup $variant="outlined">
        {selectedOption.map((step) =>
          step.subheader ? (
            <RadioOptionWithSubheaderWrapper key={step.value}>
              <RadioOptionWrapper
                id={step.value}
                name="cash-price-options"
                value={step.value}
                checked={step.checked}
                onChange={() => setSelectedOption(updatedStepsOnSelect(selectedOption, step))}
                variant="outlined"
              >
                <RadioOptionWithSubheader>
                  <RadioLabelAndSubheader>
                    <RadioLabel>{step.label}</RadioLabel>
                    <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
                      {step.subheader}
                    </Typography>
                  </RadioLabelAndSubheader>
                </RadioOptionWithSubheader>
              </RadioOptionWrapper>
            </RadioOptionWithSubheaderWrapper>
          ) : (
            <RadioOptionWrapper
              key={step.value}
              id={step.value}
              name="cash-price-options"
              value={step.value}
              checked={step.checked}
              onChange={() => setSelectedOption(updatedStepsOnSelect(selectedOption, step))}
              variant="outlined"
            >
              <RadioLabel>{step.label}</RadioLabel>
            </RadioOptionWrapper>
          ),
        )}
      </RadioBlockGroup>
      <ButtonRow align={Alignment.SpaceBetween} fullWidth>
        <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
        <Button variant="primary" size="sm" onClick={handleContinue}>
          Continue
        </Button>
      </ButtonRow>
    </ActionCard>
  );
};
